/**
 * Wake Lock Service
 * 
 * Prevents screen from locking during active sessions on mobile devices.
 * Handles background/foreground transitions to maintain WebRTC connections.
 * 
 * Features:
 * - Screen Wake Lock API integration
 * - Automatic re-acquisition after visibility changes
 * - Background state monitoring
 * - Graceful degradation for unsupported browsers
 */

import { logger } from '../utils/logger';

export type WakeLockState = 'active' | 'released' | 'unsupported';

export interface WakeLockServiceEvents {
  onStateChange: (state: WakeLockState) => void;
  onVisibilityChange: (isVisible: boolean) => void;
  onError: (error: Error) => void;
  onScreenLockWarning: () => void; // When screen is about to lock
}

class WakeLockService {
  private wakeLock: WakeLockSentinel | null = null;
  private isSupported: boolean = false;
  private events: Partial<WakeLockServiceEvents> = {};
  private state: WakeLockState = 'released';
  private isEnabled: boolean = false;

  constructor() {
    // Check if Wake Lock API is supported
    this.isSupported = 'wakeLock' in navigator;
    
    if (!this.isSupported) {
      console.warn('⚠️ Wake Lock API not supported in this browser');
      this.state = 'unsupported';
    } else {
      logger.debug('✅ Wake Lock API is supported');
      
      // Set up visibility change listener
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  /**
   * Register event listeners
   */
  on<K extends keyof WakeLockServiceEvents>(event: K, callback: WakeLockServiceEvents[K]): void {
    this.events[event] = callback;
  }

  /**
   * Remove event listeners
   */
  off<K extends keyof WakeLockServiceEvents>(event: K): void {
    delete this.events[event];
  }

  /**
   * Get current wake lock state
   */
  getState(): WakeLockState {
    return this.state;
  }

  /**
   * Check if Wake Lock API is supported
   */
  isWakeLockSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Request wake lock to prevent screen from sleeping
   */
  async request(): Promise<void> {
    if (!this.isSupported) {
      console.warn('⚠️ Wake Lock not supported - screen may lock during session');
      return;
    }

    // Don't request if already active
    if (this.wakeLock && !this.wakeLock.released) {
      logger.debug('✅ Wake Lock already active');
      return;
    }

    try {
      logger.debug('🔓 Requesting wake lock...');
      
      // @ts-ignore - Wake Lock API types may not be fully available
      this.wakeLock = await navigator.wakeLock.request('screen');
      
      this.isEnabled = true;
      this.setState('active');
      
      logger.info('✅ Wake Lock acquired - screen will stay on');

      // Handle wake lock release (happens when tab loses visibility)
      this.wakeLock.addEventListener('release', () => {
        logger.debug('🔓 Wake Lock released');
        this.setState('released');
      });

    } catch (error) {
      const err = error as Error;
      console.error('❌ Failed to acquire wake lock:', err);
      
      if (err.name === 'NotAllowedError') {
        console.warn('⚠️ Wake Lock permission denied - user may need to grant permission');
      }
      
      this.events.onError?.(err);
      this.setState('released');
    }
  }

  /**
   * Release wake lock (allow screen to sleep)
   */
  async release(): Promise<void> {
    if (!this.wakeLock || this.wakeLock.released) {
      return;
    }

    try {
      logger.debug('🔒 Releasing wake lock...');
      
      await this.wakeLock.release();
      this.wakeLock = null;
      this.isEnabled = false;
      this.setState('released');
      
      logger.info('✅ Wake Lock released - screen can sleep now');
      
    } catch (error) {
      console.error('❌ Failed to release wake lock:', error);
      this.events.onError?.(error as Error);
    }
  }

  /**
   * Handle page visibility changes
   * Re-acquire wake lock when page becomes visible again
   */
  private handleVisibilityChange = async (): Promise<void> => {
    const isVisible = !document.hidden;
    
    logger.debug(`👁️ Page visibility changed: ${isVisible ? 'VISIBLE' : 'HIDDEN'}`);
    
    // Notify listeners
    this.events.onVisibilityChange?.(isVisible);

    if (isVisible && this.isEnabled && this.isSupported) {
      // Page became visible again - re-acquire wake lock
      logger.debug('🔄 Page visible again - re-acquiring wake lock...');
      await this.request();
    } else if (!isVisible) {
      logger.debug('📱 Page hidden - app in background');
      
      // ✅ FIXED: Only show warning if wake lock was actually active when page went hidden
      // AND if we can detect it was likely a manual screen lock (not just tab switch)
      // Since we can't reliably detect screen lock vs tab switch, we'll only warn
      // if the wake lock was active AND we're on a mobile device (more likely to be screen lock)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (this.isEnabled && isMobile) {
        // On mobile, page going hidden while wake lock is active is more likely screen lock
        console.warn('⚠️ Screen may have been locked manually - session will pause');
        this.events.onScreenLockWarning?.();
      }
      // On desktop, page going hidden is usually just tab switching, so we don't warn
      
      // Wake lock is automatically released when page is hidden
      // We keep isEnabled=true so we can re-acquire when visible
    }
  };

  /**
   * Set state and notify listeners
   */
  private setState(newState: WakeLockState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.events.onStateChange?.(newState);
    }
  }

  /**
   * Cleanup - release wake lock and remove listeners
   */
  dispose(): void {
    this.release();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.events = {};
  }
}

// Export singleton instance
export default new WakeLockService();
